'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

/**
 * Avatar size variants
 */
const avatarVariants = cva(
  'inline-flex items-center justify-center overflow-hidden font-medium transition-all',
  {
    variants: {
      size: {
        sm: 'h-8 w-8 text-xs',
        md: 'h-10 w-10 text-sm',
        lg: 'h-12 w-12 text-base',
        xl: 'h-16 w-16 text-lg',
      },
      shape: {
        circle: 'rounded-full',
        rounded: 'rounded-md',
      },
    },
    defaultVariants: {
      size: 'md',
      shape: 'circle',
    },
  }
);

export interface AvatarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof avatarVariants> {
  /** Image URL for the avatar */
  src?: string | undefined;
  /** User name for generating initials and color */
  name: string;
  /** Alternative text for the image */
  alt?: string | undefined;
  /** Whether to show tooltip with name on hover */
  showTooltip?: boolean | undefined;
  /** Custom fallback content (overrides initials) */
  fallback?: React.ReactNode | undefined;
}

/**
 * Generate initials from a name
 * Takes the first letter of the first two words
 * @param name - Full name
 * @returns Initials (max 2 characters)
 */
function getInitials(name: string): string {
  if (!name) return '?';

  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();
}

/**
 * Generate a consistent background color from a name
 * Uses a hash function to ensure the same name always gets the same color
 * @param name - User name
 * @returns Tailwind background color class
 */
function getColorFromName(name: string): string {
  const colors = [
    'bg-primary-500',
    'bg-success-500',
    'bg-warning-500',
    'bg-error-500',
    'bg-info-500',
    'bg-primary-600',
    'bg-success-600',
    'bg-warning-600',
    'bg-error-600',
    'bg-info-600',
    'bg-primary-700',
    'bg-success-700',
  ];

  // Simple hash function
  const hash = name
    .toLowerCase()
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);

  return colors[hash % colors.length]!;
}

/**
 * Avatar Component
 *
 * Displays a user avatar with image or initials fallback.
 * Features:
 * - Multiple sizes (sm, md, lg, xl)
 * - Circular or rounded shape
 * - Automatic initials generation from name
 * - Consistent color generation based on name hash
 * - Image error handling with fallback to initials
 * - Optional tooltip on hover
 * - Loading state support
 * - Full accessibility support
 *
 * @example
 * ```tsx
 * // Basic avatar with initials
 * <Avatar name="John Doe" />
 *
 * // Avatar with image
 * <Avatar name="John Doe" src="/avatars/john.jpg" />
 *
 * // Different sizes
 * <Avatar name="Jane Smith" size="sm" />
 * <Avatar name="Jane Smith" size="lg" />
 *
 * // Rounded corners
 * <Avatar name="Bob Wilson" shape="rounded" />
 *
 * // With tooltip
 * <Avatar name="Alice Johnson" showTooltip />
 *
 * // Custom fallback
 * <Avatar name="Admin" fallback={<AdminIcon />} />
 * ```
 */
const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      size,
      shape,
      src,
      name,
      alt,
      showTooltip = false,
      fallback,
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(!!src);

    // Reset error state when src changes
    React.useEffect(() => {
      setImageError(false);
      setIsLoading(!!src);
    }, [src]);

    const initials = getInitials(name);
    const backgroundColor = getColorFromName(name);
    const showImage = src && !imageError;
    const displayName = alt || name;

    const avatarContent = (
      <div
        ref={ref}
        className={cn(
          avatarVariants({ size, shape }),
          !showImage && backgroundColor,
          !showImage && 'text-white',
          className
        )}
        role="img"
        aria-label={displayName}
        {...props}
      >
        {showImage ? (
          <>
            <img
              src={src}
              alt={displayName}
              className={cn(
                'h-full w-full object-cover',
                isLoading && 'opacity-0'
              )}
              onError={() => {
                setImageError(true);
                setIsLoading(false);
              }}
              onLoad={() => setIsLoading(false)}
            />
            {isLoading && (
              <div className="absolute inset-0 animate-pulse bg-neutral-200 dark:bg-neutral-700" />
            )}
          </>
        ) : (
          <span className="select-none" aria-hidden="true">
            {fallback || initials}
          </span>
        )}
      </div>
    );

    if (showTooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{avatarContent}</TooltipTrigger>
            <TooltipContent>
              <p>{name}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return avatarContent;
  }
);

Avatar.displayName = 'Avatar';

/**
 * AvatarGroup Component
 *
 * Displays multiple avatars in a horizontal stack with overlap.
 * Shows a count indicator when max is exceeded.
 *
 * @example
 * ```tsx
 * <AvatarGroup max={3}>
 *   <Avatar name="John Doe" />
 *   <Avatar name="Jane Smith" />
 *   <Avatar name="Bob Wilson" />
 *   <Avatar name="Alice Johnson" />
 * </AvatarGroup>
 * ```
 */
export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Maximum number of avatars to display before showing count */
  max?: number;
  /** Size of avatars in the group */
  size?: AvatarProps['size'];
  /** Children avatars */
  children: React.ReactNode;
}

const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  ({ className, max = 5, size = 'md', children, ...props }, ref) => {
    const childArray = React.Children.toArray(children);
    const displayChildren = max ? childArray.slice(0, max) : childArray;
    const surplus = max && childArray.length > max ? childArray.length - max : 0;

    return (
      <div
        ref={ref}
        className={cn('flex items-center -space-x-2', className)}
        {...props}
      >
        {displayChildren.map((child, index) => (
          <div
            key={index}
            className="ring-2 ring-white dark:ring-neutral-900 transition-transform hover:scale-110 hover:z-10"
            style={{ zIndex: displayChildren.length - index }}
          >
            {React.isValidElement(child)
              ? React.cloneElement(child as React.ReactElement<AvatarProps>, {
                  size,
                })
              : child}
          </div>
        ))}
        {surplus > 0 && (
          <div
            className={cn(
              avatarVariants({ size, shape: 'circle' }),
              'bg-neutral-200 text-neutral-700 ring-2 ring-white dark:bg-neutral-700 dark:text-neutral-300 dark:ring-neutral-900'
            )}
            style={{ zIndex: 0 }}
          >
            <span className="select-none">+{surplus}</span>
          </div>
        )}
      </div>
    );
  }
);

AvatarGroup.displayName = 'AvatarGroup';

export { Avatar, AvatarGroup, avatarVariants };
